import { differenceInDays, parseISO } from 'date-fns'
import deepEqual from 'fast-deep-equal'
import * as qs from 'qs'
import { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { maxSelectableDays } from 'src/features/scheduledReportSummary/ScheduledReportSummary'
import { isValidDateString } from 'src/util/datetime/isValidDateString'
import { removeMatchingKeysAndValues } from 'src/util/removeMatchingKeysAndValues'

/**
 * スケジュール実施状況群を取得する際の絞り込み条件
 *
 * `dateRange` / `placeNodeUuids` はサーバーサイド絞り込み（API リクエストに含まれる）、
 * `scheduleNameKeyword` はクライアントサイド絞り込み（取得済みデータを画面側で絞る）として扱う。
 */
export type ScheduledReportSummaryFilters = {
  /**
   * 開始日と終了日を持つ配列
   * ISO8601 拡張形式の日付 (YYYY-MM-DD) により表現される
   */
  dateRange: [string, string] | null
  placeNodeUuids: string[]
  /**
   * スケジュール名による部分一致絞り込み用のキーワード（クライアントサイド適用）
   *
   * API リクエスト・queryKey には含めない。取得済み summaries を画面側で絞るためにのみ使用する。
   * 空文字列の場合は絞り込みなしとして扱う。
   */
  scheduleNameKeyword: string
}

export const initialFilters: ScheduledReportSummaryFilters = {
  dateRange: null,
  placeNodeUuids: [],
  scheduleNameKeyword: '',
}

const qsOptions = {
  parse: { ignoreQueryPrefix: true },
  stringify: {
    skipNulls: true,
    encode: false,
  },
}

/**
 * 型の保証と不正な値のデフォルト値へのフォールバックを行う
 */
const sanitize = {
  dateRange: (arg: unknown): ScheduledReportSummaryFilters['dateRange'] => {
    const [arg1, arg2] = Array.isArray(arg) ? arg : []
    if (!isValidDateString(arg1) || !isValidDateString(arg2)) {
      return initialFilters.dateRange
    }
    const dateFrom = parseISO(arg1)
    const dateTo = parseISO(arg2)
    if (
      // 日付が未来であってはならない
      dateFrom > new Date() ||
      dateTo > new Date() ||
      // 開始日が終了日より後の日付であってはならない
      dateFrom > dateTo ||
      // 日付の差が最大選択可能日数を超えてはならない
      differenceInDays(dateTo, dateFrom) >= maxSelectableDays
    ) {
      return initialFilters.dateRange
    }
    return [arg1, arg2]
  },
  placeNodeUuids: (arg: unknown): string[] => {
    if (Array.isArray(arg)) {
      return arg.filter((uuid): uuid is string => typeof uuid === 'string')
    }
    return initialFilters.placeNodeUuids
  },
  scheduleNameKeyword: (arg: unknown): string => {
    if (typeof arg === 'string') return arg
    return initialFilters.scheduleNameKeyword
  },
}

export const useFilters = () => {
  const history = useHistory()
  const location = useLocation()

  const getFiltersFromQueryString = (): ScheduledReportSummaryFilters => {
    const rawFilters = qs.parse(location.search, qsOptions.parse)
    return {
      dateRange: sanitize.dateRange(rawFilters['dateRange']),
      placeNodeUuids: sanitize.placeNodeUuids(rawFilters['placeNodeUuids']),
      scheduleNameKeyword: sanitize.scheduleNameKeyword(
        rawFilters['scheduleNameKeyword'],
      ),
    }
  }

  const [filters, setFilters] = useState<ScheduledReportSummaryFilters>(() =>
    getFiltersFromQueryString(),
  )

  // URL更新はsetterではなく下のuseEffectが行うため、更新方法はrefで受け渡す
  const historyUpdateModeRef = useRef<'push' | 'replace'>('push')

  // 絞り込みが変更されたらURLを更新する
  useEffect(() => {
    // URLが散らかるのを防ぎたいため、初期値と同じ絞り込みはURLから取り除く
    const filtersToSet = removeMatchingKeysAndValues(filters, initialFilters)
    const newQueryString = qs.stringify(filtersToSet, qsOptions.stringify)

    const historyUpdateMode = historyUpdateModeRef.current
    historyUpdateModeRef.current = 'push'

    const isAlreadySynchronized =
      newQueryString === history.location.search.substring(1)
    if (isAlreadySynchronized) return

    history[historyUpdateMode]({ search: newQueryString })
  }, [filters, history])

  // URLが変更されたら絞り込みを更新する
  useEffect(
    () => {
      const newFilters = getFiltersFromQueryString()

      const isAlreadySynchronized = deepEqual(newFilters, filters)
      if (isAlreadySynchronized) return

      // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
      setFilters(newFilters)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // getFiltersFromQueryString,
      location.search,
    ],
  )

  /**
   * 絞り込みの値を更新する
   *
   * 型のおかげで本来はsanitize関数が不要なプロパティもあるものの、
   * 一貫性を重視してすべてのプロパティに一括してsanitize関数を適用している。
   */
  const setFilterOf = {
    dateRange: (dateFrom: string, dateTo: string) => {
      setFilters(prev => ({
        ...prev,
        dateRange: sanitize.dateRange([dateFrom, dateTo]),
      }))
    },
    /**
     * @param options.replaceHistory ユーザー操作を伴わない自動補正で指定する。
     *   履歴を積むと、補正前のURLに戻るたびに補正が再実行されて押し戻されるため
     *   それより前へ戻れなくなる。
     */
    placeNodeUuids: (
      placeNodeUuids: string[],
      options?: { replaceHistory?: boolean },
    ) => {
      if (options?.replaceHistory) {
        historyUpdateModeRef.current = 'replace'
      }
      setFilters(prev => ({
        ...prev,
        placeNodeUuids: sanitize.placeNodeUuids(placeNodeUuids),
      }))
    },
    scheduleNameKeyword: (scheduleNameKeyword: string) => {
      setFilters(prev => ({
        ...prev,
        scheduleNameKeyword: sanitize.scheduleNameKeyword(scheduleNameKeyword),
      }))
    },
  }

  const resetFilterOf = {
    dateRange: () => {
      setFilters(prev => ({
        ...prev,
        dateRange: null,
      }))
    },
    /**
     * ドロワー内の絞り込みのみリセットする。
     *
     * ドロワー外で操作される `dateRange` と `scheduleNameKeyword` は現在値を保持する。
     */
    allExceptInDrawer: () => {
      setFilters(current => ({
        ...initialFilters,
        dateRange: current.dateRange,
        scheduleNameKeyword: current.scheduleNameKeyword,
      }))
    },
  }

  /**
   * ドロワー内の絞り込みが何か有効になっているか
   *
   * ドロワー外で操作される `dateRange` と `scheduleNameKeyword` は判定対象から除外する。
   */
  const isSomeFilterInDrawerActive = !deepEqual(
    { placeNodeUuids: filters.placeNodeUuids },
    { placeNodeUuids: initialFilters.placeNodeUuids },
  )

  return {
    filters,
    isSomeFilterInDrawerActive,
    resetFilterOf,
    setFilterOf,
  }
}
